import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  FaUserEdit,
  FaSlidersH,
  FaIdCard,
  FaCreditCard,
  FaBan,
  FaTrophy,
  FaBlog,
  FaBars,
  FaTimes,
  FaChevronLeft,
} from "react-icons/fa";
import "./Adminsidebar.css";

// Menu data — edit names, paths, or icons here.
const menuItems = [
  {
    section: "Home",
    items: [
      {
        name: "Dashboard",
        path: "/admin-html-secure-metro",
        icon: <FaUserEdit />,
      },
    ],
  },
  {
    section: "User Details",
    items: [
      {
        name: "Users",
        path: "/admin-html-secure-metro/users",
        icon: <FaSlidersH />,
      },
      {
        name: "Membership Plan",
        path: "/admin-html-secure-metro/plan",
        icon: <FaIdCard />,
      },
      {
        name: "Membership Purchase",
        path: "/admin-html-secure-metro/purchase",
        icon: <FaCreditCard />,
      },
    ],
  },
  {
    section: "Profile Details",
    items: [
      {
        name: "Delete Profile",
        path: "/admin-html-secure-metro/delete",
        icon: <FaBan />,
      },
      {
        name: "Success Story",
        path: "/admin-html-secure-metro/story",
        icon: <FaTrophy />,
      },
      {
        name: "Contact Details",
        path: "/admin-html-secure-metro/contact",
        icon: <FaUserEdit />,
      },
      {
        name: "Blog Table",
        path: "/admin-html-secure-metro/blogtable",
        icon: <FaBlog />,
      },
      {
        name: "Pop up",
        path: "/admin-html-secure-metro/popup",
        icon: <FaBlog />,
      },
    ],
  },
];

/**
 * AdminSidebar
 * Responsive admin navigation:
 *  - Desktop (>1024px): full sidebar, manually collapsible to icon rail.
 *  - Tablet (769–1024px): same component, narrower spacing via CSS.
 *  - Mobile (<=768px): hidden off-canvas, opened with the hamburger button,
 *    closes on backdrop tap or route change.
 *
 * Drop <AdminSidebar /> once near the root of your admin layout, and give
 * the sibling content area `margin-left: var(--admin-sidebar-current-width)`
 * (see the comment at the bottom of AdminSidebar.css) so content isn't
 * covered on desktop.
 */
const AdminSidebar = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Prevent background scroll while the mobile drawer is open.
  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileOpen]);

  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 768px)");

    const syncWidth = () => {
      const width = mobileQuery.matches ? 0 : isCollapsed ? 40 : 250;
      document.documentElement.style.setProperty(
        "--admin-sidebar-current-width",
        `${width}px`,
      );
    };

    syncWidth();
    mobileQuery.addEventListener("change", syncWidth);
    return () => mobileQuery.removeEventListener("change", syncWidth);
  }, [isCollapsed]);

  return (
    <>
      <button
        type="button"
        className="admin-sidebar-mobile-toggle"
        onClick={() => setIsMobileOpen(true)}
        aria-label="Open admin menu"
      >
        <FaBars />
      </button>

      {isMobileOpen && (
        <div
          className="admin-sidebar-overlay"
          onClick={() => setIsMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={[
          "admin-sidebar",
          isCollapsed ? "admin-sidebar--collapsed" : "",
          isMobileOpen ? "admin-sidebar--mobile-open" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="admin-sidebar-header">
          <div className="admin-sidebar-brand">
            <span className="admin-sidebar-mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" className="admin-sidebar-mark-svg">
                <rect
                  x="3"
                  y="3"
                  width="18"
                  height="18"
                  rx="2"
                  transform="rotate(45 12 12)"
                  className="admin-sidebar-mark-shape"
                />
              </svg>
            </span>
            {!isCollapsed && (
              <span className="admin-sidebar-brand-text">Matrimony Admin</span>
            )}
          </div>

          <button
            type="button"
            className="admin-sidebar-close-btn"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Close admin menu"
          >
            <FaTimes />
          </button>

          <button
            type="button"
            className={`admin-sidebar-collapse-btn ${
              isCollapsed ? "admin-sidebar-collapse-btn--collapsed" : ""
            }`}
            onClick={() => setIsCollapsed((prev) => !prev)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <FaChevronLeft />
          </button>
        </div>

        <nav className="admin-sidebar-nav">
          {menuItems.map((section) => (
            <div className="admin-sidebar-section" key={section.section}>
              {!isCollapsed && (
                <p className="admin-sidebar-section-title">{section.section}</p>
              )}
              <ul className="admin-sidebar-list">
                {section.items.map((item) => (
                  <li className="admin-sidebar-item" key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === "/admin-html-secure-metro"}
                      className={({ isActive }) =>
                        `admin-sidebar-link ${
                          isActive ? "admin-sidebar-link--active" : ""
                        }`
                      }
                      title={isCollapsed ? item.name : undefined}
                    >
                      <span className="admin-sidebar-icon">{item.icon}</span>
                      {!isCollapsed && (
                        <span className="admin-sidebar-text">{item.name}</span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};

export default AdminSidebar;
